import { CarouselItem } from '@synoptic/ui-kit/carousel/carousel.js';
import { SelectionTagGroup } from '@synoptic/ui-kit/selection-tag/selection-tag-group.js';
import { SelectionTagGroupItem } from '@synoptic/ui-kit/selection-tag/selection-tag-group.js';
import clsx from 'clsx';
import { useState } from 'react';
import { StreamPricingType } from '@/__generated__/graphql.ts';
import { paths } from '@/routes-utils/paths.ts';
import { useBestDeals } from '../../use-best-deals.ts';
import { useNewStreams } from '../../use-new-streams.ts';
import { useStreamsWeLove } from '../../use-streams-we-love.ts';
import { HomeCarousel, HomeSectionHeader } from '../home-carousel.tsx';
import {
  carouselItemHalfWidth,
  homeExploreSection,
  carouselItemQuarterWidth,
  carouselItemFullWidth,
} from '../home-sections.css.ts';
import { HomeStreamCardMini } from './home-stream-card-mini.tsx';
import { HomeStreamCard } from './home-stream-card.tsx';
import { HomeStreamPackageCard } from './home-stream-package-card.tsx';
import {
  streamTypeLabel,
  streamTypeLabelSelected,
} from './home-streams-section.css.ts';

export const HomeStreamsSection = () => {
  const [selectedStreamPricingType, setSelectedStreamPricingType] =
    useState<StreamPricingType>(StreamPricingType.Paid);

  const {
    streams: streamsWeLove,
    loading: isLoadingStreamsWeLove,
    areStreamsWeLoveEmpty,
  } = useStreamsWeLove();
  const {
    streamPackages,
    loading: isLoadingStreamPackages,
    areStreamPackagesEmpty,
  } = useBestDeals();

  const {
    streams: newStreams,
    loading: isLoadingNewStreams,
    areNewStreamsEmpty,
  } = useNewStreams({
    pricingType: selectedStreamPricingType,
  });

  if (areStreamsWeLoveEmpty && areStreamPackagesEmpty && areNewStreamsEmpty) {
    return null;
  }

  return (
    <div className={homeExploreSection}>
      {!areStreamsWeLoveEmpty && (
        <HomeCarousel
          skeleton={{
            height: 227,
            variant: 'half',
            show: isLoadingStreamsWeLove,
          }}
          showAllUrl={paths.streams}
          title="Data streams we love"
        >
          {streamsWeLove?.map((stream) => (
            <CarouselItem key={stream.id} className={carouselItemHalfWidth}>
              <HomeStreamCard stream={stream} />
            </CarouselItem>
          ))}
        </HomeCarousel>
      )}
      {!areStreamPackagesEmpty && (
        <HomeCarousel
          skeleton={{
            height: 355,
            variant: 'full',
            show: isLoadingStreamPackages,
          }}
          showAllUrl={paths.streamPackages}
          title="Best deals for you"
        >
          {streamPackages?.map((streamPackage) => (
            <CarouselItem
              key={streamPackage.id}
              className={carouselItemFullWidth}
            >
              <HomeStreamPackageCard streamPackage={streamPackage} />
            </CarouselItem>
          ))}
        </HomeCarousel>
      )}
      {!areNewStreamsEmpty && (
        <div>
          <HomeSectionHeader title="New Streams">
            <SelectionTagGroup
              type={'single'}
              value={selectedStreamPricingType}
              onValueChange={(value) =>
                setSelectedStreamPricingType(
                  (value as StreamPricingType) || StreamPricingType.Paid,
                )
              }
            >
              <SelectionTagGroupItem
                className={clsx(streamTypeLabel, {
                  [streamTypeLabelSelected]:
                    selectedStreamPricingType === StreamPricingType.Paid,
                })}
                value={StreamPricingType.Paid}
                aria-label="Toggle paid"
              >
                Paid
              </SelectionTagGroupItem>
              <SelectionTagGroupItem
                className={clsx(streamTypeLabel, {
                  [streamTypeLabelSelected]:
                    selectedStreamPricingType === StreamPricingType.Free,
                })}
                value={StreamPricingType.Free}
                aria-label="Toggle free"
              >
                Free
              </SelectionTagGroupItem>
            </SelectionTagGroup>
          </HomeSectionHeader>
          <HomeCarousel
            skeleton={{
              height: 120,
              variant: 'quarter',
              show: isLoadingNewStreams,
            }}
          >
            {newStreams?.map((stream) => (
              <CarouselItem
                key={stream.id + '-new-stream'}
                className={carouselItemQuarterWidth}
              >
                <HomeStreamCardMini stream={stream} />
              </CarouselItem>
            ))}
          </HomeCarousel>
        </div>
      )}
    </div>
  );
};
